// Cognito SRP wrapper for the single-user dashboard.
//
// Wraps amazon-cognito-identity-js so the rest of the app never touches the SDK
// directly. The pool is configured from VITE_COGNITO_* build-time env vars (the user
// pool id and app client id are Terraform outputs; the app client has no secret, so
// SRP runs entirely in the browser).
//
// We surface the Cognito *id token* as the API bearer: the API Gateway JWT authorizer
// is configured with `audience = [app client id]`, and only the id token carries
// `aud = <client id>` (plus the `sub` the backend uses for the USER# partition key).

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  type ISignUpResult,
} from "amazon-cognito-identity-js";

export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
}

/** Read the pool config from Vite env. Returns null when auth is not configured. */
export function cognitoConfig(): CognitoConfig | null {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
  if (!userPoolId || !clientId) return null;
  return { userPoolId, clientId };
}

let cachedPool: CognitoUserPool | null = null;

function userPool(): CognitoUserPool {
  const config = cognitoConfig();
  if (!config) {
    throw new Error(
      "Cognito is not configured (set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID)",
    );
  }
  if (cachedPool && cachedPool.getUserPoolId() === config.userPoolId) {
    return cachedPool;
  }
  cachedPool = new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.clientId,
  });
  return cachedPool;
}

/** Raised when sign-in succeeds but Cognito requires a new permanent password. */
export class NewPasswordRequiredError extends Error {
  readonly user: CognitoUser;
  readonly userAttributes: Record<string, string>;

  constructor(user: CognitoUser, userAttributes: Record<string, string>) {
    super("a new password is required to complete sign-in");
    this.name = "NewPasswordRequiredError";
    this.user = user;
    this.userAttributes = userAttributes;
  }
}

/**
 * Raised when the user has a registered TOTP authenticator and must enter a 6-digit
 * code to complete sign-in. Hold the `user` to submit the code via `submitTotpCode`.
 */
export class TotpRequiredError extends Error {
  readonly user: CognitoUser;

  constructor(user: CognitoUser) {
    super("a one-time code from your authenticator app is required");
    this.name = "TotpRequiredError";
    this.user = user;
  }
}

/**
 * Raised when MFA is required by the pool but the user has not yet registered an
 * authenticator. Carries the shared secret and an otpauth:// URI (for a QR code) so the
 * UI can show enrollment, then complete it with `verifyTotpSetup`.
 */
export class TotpSetupRequiredError extends Error {
  readonly user: CognitoUser;
  readonly secretCode: string;
  readonly otpauthUri: string;

  constructor(user: CognitoUser, secretCode: string, email: string) {
    super("set up an authenticator app to finish signing in");
    this.name = "TotpSetupRequiredError";
    this.user = user;
    this.secretCode = secretCode;
    // Standard otpauth URI so an authenticator app (or a rendered QR) can import it.
    const issuer = encodeURIComponent("smart-margin");
    const label = encodeURIComponent(email);
    this.otpauthUri =
      `otpauth://totp/${issuer}:${label}?secret=${secretCode}&issuer=${issuer}`;
  }
}

function cognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: userPool() });
}

/**
 * Sign in with SRP. Resolves with the authenticated session, or rejects with a
 * NewPasswordRequiredError (invite-only pools issue a temporary password that must be
 * replaced on first sign-in).
 */
export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  const user = cognitoUser(email);
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        // These attributes cannot be re-submitted on the challenge response.
        delete userAttributes.email_verified;
        delete userAttributes.email;
        reject(new NewPasswordRequiredError(user, userAttributes));
      },
      // The user already has a TOTP authenticator; ask for the 6-digit code.
      totpRequired: () => {
        reject(new TotpRequiredError(user));
      },
      // MFA is required but no authenticator is registered yet: begin enrollment by
      // asking Cognito for a software-token secret, then surface it for the UI.
      mfaSetup: () => {
        user.associateSoftwareToken({
          associateSecretCode: (secretCode: string) => {
            reject(new TotpSetupRequiredError(user, secretCode, email));
          },
          onFailure: (err) => reject(err),
        });
      },
    });
  });
}

/**
 * Submit a 6-digit TOTP code for the SOFTWARE_TOKEN_MFA challenge (the `totpRequired`
 * path), resolving with the authenticated session.
 */
export function submitTotpCode(
  user: CognitoUser,
  code: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
      },
      "SOFTWARE_TOKEN_MFA",
    );
  });
}

/**
 * Turn a Cognito authenticator-code failure into a message that tells the user what to
 * actually do. Cognito's raw text for a rejected TOTP code is "Invalid code received for
 * user", which reads like a typo even when the user copied the code correctly. The usual
 * cause is clock drift between the authenticator device and real time (TOTP codes are
 * derived from the current 30-second window), so we point the user there. Non-TOTP errors
 * pass through unchanged.
 */
export function totpErrorMessage(err: unknown): string {
  const name = err instanceof Error ? err.name : "";
  if (name === "CodeMismatchException" || name === "ExpiredCodeException") {
    return (
      "That authenticator code was not accepted. This usually means your device clock is " +
      "out of sync. Enable automatic/network time on your phone (in Google Authenticator, " +
      "Settings, Time correction for codes, Sync now), then enter a fresh code."
    );
  }
  if (err instanceof Error && err.message) return err.message;
  return "Invalid code";
}

/**
 * Complete first-time TOTP enrollment (the `mfaSetup` path): verify the code the user
 * generated from the associated secret, enable it as their preferred MFA, and resolve
 * with the authenticated session.
 */
export function verifyTotpSetup(
  user: CognitoUser,
  code: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.verifySoftwareToken(code, "smart-margin authenticator", {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

/** Complete the NEW_PASSWORD_REQUIRED challenge, returning the authenticated session. */
export function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string>,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

export interface TotpReassociation {
  /** Fresh shared secret for the new authenticator. */
  secretCode: string;
  /** otpauth:// URI for the new secret, to render as a QR code. */
  otpauthUri: string;
  /** Confirm a 6-digit code from the new authenticator to finish rotating. */
  verify: (code: string) => Promise<void>;
}

/**
 * Begin re-registering a TOTP authenticator for the already-signed-in user. Returns a
 * fresh secret + otpauth URI (for a new QR) and a `verify` step that confirms the first
 * code and makes the new authenticator the user's software-token MFA.
 *
 * Lets a user move to a new phone / authenticator app without being locked out, since
 * the one-time MFA_SETUP challenge only appears before the first enrollment.
 */
export function reassociateTotp(email: string): Promise<TotpReassociation> {
  const user = userPool().getCurrentUser();
  if (!user) {
    return Promise.reject(new Error("not signed in"));
  }
  return new Promise((resolve, reject) => {
    // associateSoftwareToken requires a hydrated session on the cached user.
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        reject(err ?? new Error("session is not valid; sign in again"));
        return;
      }
      user.associateSoftwareToken({
        associateSecretCode: (secretCode: string) => {
          const issuer = encodeURIComponent("smart-margin");
          const label = encodeURIComponent(email);
          const otpauthUri =
            `otpauth://totp/${issuer}:${label}?secret=${secretCode}&issuer=${issuer}`;
          resolve({
            secretCode,
            otpauthUri,
            verify: (code: string) =>
              new Promise<void>((res, rej) => {
                user.verifySoftwareToken(code, "smart-margin authenticator", {
                  onSuccess: () => res(),
                  onFailure: (e) => rej(e),
                });
              }),
          });
        },
        onFailure: (e) => reject(e),
      });
    });
  });
}

// Dashboard views can start several API requests together. Coalesce their session lookup
// so an expired token results in one Cognito refresh rather than one refresh per request.
let currentSessionPromise: Promise<CognitoUserSession | null> | null = null;

/** Return the current cached user (if any) with a valid, auto-refreshed session. */
export function currentSession(): Promise<CognitoUserSession | null> {
  if (currentSessionPromise) return currentSessionPromise;
  const user = userPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  const pending = new Promise<CognitoUserSession | null>((resolve) => {
    // getSession refreshes the id/access tokens from the refresh token when expired.
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session);
    });
  });
  currentSessionPromise = pending;
  void pending.finally(() => {
    if (currentSessionPromise === pending) currentSessionPromise = null;
  });
  return pending;
}

/**
 * Return a currently-valid id token for the API bearer, refreshing it from the refresh
 * token when the previous one has expired. Returns null when there is no restorable
 * session (not signed in, or the refresh token itself has expired / been revoked), which
 * the caller treats as "sign the user out and send them to login".
 *
 * This is the per-request token source: the id token is short-lived (1 hour), so reading
 * a cached copy eventually yields a 401. Going through getSession on each request lets the
 * SDK silently mint a fresh id token from the long-lived refresh token instead.
 */
export function freshIdToken(): Promise<string | null> {
  return currentSession().then((session) => (session ? idToken(session) : null));
}

/** Sign the current user out locally and invalidate their tokens. */
export function signOut(): void {
  const user = userPool().getCurrentUser();
  user?.signOut();
}

/** Extract the id token (sent as the API bearer) from a session. */
export function idToken(session: CognitoUserSession): string {
  return session.getIdToken().getJwtToken();
}

/**
 * Self-service sign-up: register a new user with email + password. The pool emails a
 * verification code; the account is not usable until confirmed via confirmSignUp.
 * Resolves on success; rejects with the Cognito error (e.g. UsernameExistsException,
 * InvalidPasswordException) otherwise.
 */
export function signUp(email: string, password: string): Promise<ISignUpResult> {
  const pool = userPool();
  const attributes = [
    new CognitoUserAttribute({ Name: "email", Value: email }),
  ];
  return new Promise((resolve, reject) => {
    pool.signUp(email, password, attributes, [], (err, result) => {
      if (err || !result) {
        reject(err ?? new Error("sign-up failed"));
        return;
      }
      resolve(result);
    });
  });
}

/** Confirm a new account with the emailed verification code. */
export function confirmSignUp(email: string, code: string): Promise<void> {
  const user = cognitoUser(email);
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

/** Resend the email verification code for an unconfirmed account. */
export function resendConfirmationCode(email: string): Promise<void> {
  const user = cognitoUser(email);
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

/** Best-effort email of the current signed-in user, read from the id-token claims. */
export function currentEmail(): Promise<string | null> {
  const user = userPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      const email = session.getIdToken().payload.email;
      resolve(typeof email === "string" ? email : null);
    });
  });
}

export type { CognitoUser, CognitoUserSession, ISignUpResult };
